<template>
  <article
    ref="root"
    class="neu-card h-100"
    :class="{
      'neu-card--featured': featured,
      'neu-card--inset': inset,
      'nm-sheen': !inset,
    }"
    :style="inset ? undefined : surfaceStyle"
    @pointerenter="!inset && onPointerEnter($event)"
    @pointermove="!inset && onPointerMove($event)"
    @pointerleave="!inset && onPointerLeave()"
  >
    <div v-if="chip" class="mb-3">
      <span class="nm-chip" :class="{ 'nm-chip--featured': featured }">
        <i v-if="chipIcon" :class="chipIcon" class="nm-chip__icon" aria-hidden="true" />
        {{ chip }}
      </span>
    </div>
    <h3 class="neu-card__title mb-2">{{ title }}</h3>
    <p class="neu-card__body mb-4">{{ body }}</p>
    <RouterLink v-if="to" :to="to" class="nm-btn nm-btn--ghost nm-btn--sm">
      {{ cta }}
      <i class="fa-solid fa-arrow-right" aria-hidden="true" />
    </RouterLink>
  </article>
</template>

<script setup>
import { useCardSheen } from '@/composables/useCardSheen'

defineProps({
  title: { type: String, required: true },
  body: { type: String, required: true },
  chip: { type: String, default: '' },
  chipIcon: { type: String, default: '' },
  cta: { type: String, default: 'Learn more' },
  to: { type: String, default: '' },
  featured: { type: Boolean, default: false },
  inset: { type: Boolean, default: false },
})

const { root, surfaceStyle, onPointerMove, onPointerEnter, onPointerLeave } = useCardSheen()
</script>

<style scoped>
.neu-card {
  position: relative;
  overflow: visible;
  isolation: isolate;
  padding: 1.25rem;
  background: var(--nm-surface);
  border-radius: var(--nm-radius-lg);
  box-shadow: var(--nm-raised-md);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition:
    transform var(--nm-duration) var(--nm-ease-out),
    box-shadow var(--nm-duration) var(--nm-ease-out),
    border-color var(--nm-duration) var(--nm-ease-out);
}

.neu-card:hover:not(.neu-card--inset) {
  transform: translateY(-4px);
  box-shadow: var(--nm-raised-lg);
  border-color: rgba(255, 255, 255, 0.07);
  z-index: 1;
}

.neu-card--featured {
  background: linear-gradient(155deg, var(--nm-surface-raised) 0%, var(--nm-surface) 100%);
  box-shadow: var(--nm-raised-lg), var(--nm-glow-accent);
  border-color: rgba(255, 255, 255, 0.08);
}

.neu-card--featured:hover {
  transform: translateY(-5px);
  box-shadow: var(--nm-raised-xl), var(--nm-glow-accent);
}

.neu-card--inset {
  background: var(--nm-surface-inset) !important;
  box-shadow: var(--nm-inset-md);
}

.neu-card--inset:hover {
  transform: none;
  box-shadow: var(--nm-inset-md);
}

@media (min-width: 768px) {
  .neu-card {
    padding: 2rem;
  }
}

.neu-card__title {
  font-size: 1.2rem;
  font-weight: var(--nm-font-weight-semibold);
  letter-spacing: -0.01em;
}

.neu-card__body {
  font-size: 0.95rem;
  color: var(--nm-text-muted);
  line-height: 1.6;
  margin-bottom: 0;
}

.nm-chip__icon {
  margin-right: 0.35rem;
  font-size: 0.72rem;
  opacity: 0.9;
}
</style>